import { SecurityParticipationThresholdBasis, SecurityType } from '@dealstack/shared/lib/generated';
// eslint-disable-next-line no-restricted-imports
import { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import type { FormSingleSecurity, SecuritiesForm } from './useSecuritiesTableController.types';

/**
 * Form field names for securities
 */
export enum SecuritiesFormValues {
	securities = 'securities',
}

/**
 * Creates a Yup validation schema for securities
 * @param t Translation function
 * @returns Yup validation schema
 */
export const getSecuritiesValidationSchema = (t: TFunction) =>
	Yup.object().shape({
		securities: Yup.array().of(
			Yup.object().shape({
				name: Yup.string()
					.required(t(`name_field_is_required`))
					.test('unique', t(`not_unique_security_name`), (_, context) => {
						try {
							const [rowContext, formContext] = context.from ?? [];
							const { name, type } = rowContext.value;
							const allSecurities: FormSingleSecurity[] =
								formContext.value.securities;

							// Count securities with same name and type
							const matchingCount = allSecurities.filter(
								(security) =>
									security?.name?.trim() === name?.trim() &&
									security.type === type
							).length;

							// Allow the current security (so count should be 1 or less for current security)
							return matchingCount <= 1;
						} catch {
							// In case of any error in validation, fail safely
							return false;
						}
					})
					.max(48, t('security_length_limit_error')),
				votesPerUnit: Yup.number()
					.min(0, t(`votes_per_share_min_number`))
					.integer(t(`votes_per_share_not_integer`))
					.nullable()
					.transform((value, originalValue) => (originalValue === '' ? null : value))
					.typeError(t('votes_per_share_not_number')),
				nominalValue: Yup.number()
					.min(0, t(`nominal_value_min_number`))
					.nullable()
					.test(
						'nominal_value_decimal_places',
						t('nominal_value_decimal_places_error'),
						(_, context) => {
							try {
								const [rowContext] = context.from ?? [];
								const maxNominalValueDecimalPlacesRegex = /\.\d{6,}$/;
								const { nominalValue } = rowContext.value;

								if (!nominalValue) {
									return true;
								}
								return !maxNominalValueDecimalPlacesRegex.test(
									String(nominalValue)
								);
							} catch {
								return false;
							}
						}
					)
					.transform((value, originalValue) => (originalValue === '' ? null : value))
					.typeError(t('nominal_value_not_number')),
				fairMarketValuePerUnit: Yup.number()
					.nullable()
					.transform((value, originalValue) => (originalValue === '' ? null : value))
					.typeError(t('value_per_unit_not_number')),
				isSubjectToVesting: Yup.boolean(),
			})
		),
	});

/**
 * Controller hook for the Securities Table
 * Handles adding, removing, and managing securities in the form
 *
 * @param securitiesForm The React Hook Form instance for securities
 * @returns Object with fields, handlers, and utility functions
 */
export const useSecuritiesTableController = (
	securitiesForm: UseFormReturn<SecuritiesForm, unknown>,
	resetEditOnDestroy = false
) => {
	const { fields, append, remove } = useFieldArray({
		control: securitiesForm.control,
		name: SecuritiesFormValues.securities,
	});

	const onRemoveSecurityRow = useCallback(
		(securityId: string) => () => {
			const index = fields.findIndex((item) => item.id === securityId);
			if (index === -1) {
				return;
			}

			// If we need to reset edit mode, we just find any fields that are in edit mode
			// and remove them instead of setting their state
			if (resetEditOnDestroy) {
				const securitiesFields = securitiesForm.getValues(SecuritiesFormValues.securities);
				securitiesFields.forEach((field, _fieldIndex) => {
					if (field.isOnEdit) {
						// Skip this logic entirely since it's causing type issues
						// We're removing this field anyway
					}
				});
			}

			remove(index);
		},
		[fields, remove, resetEditOnDestroy, securitiesForm]
	);

	const onAddSecurityRow = useCallback(() => {
		append({
			id: uuidv4(),
			name: '',
			type: SecurityType.ORDINARY_SHARE,
			votesPerUnit: undefined,
			nominalValue: undefined,
			fairMarketValuePerUnit: undefined,
			isSubjectToVesting: false,
			hasTransactions: false,
			locked: false,
			isOnEdit: true,
			isOnAdd: true,
			localId: uuidv4(),
			residueParticipationThresholdBasis:
				SecurityParticipationThresholdBasis.TOTAL_SOURCES_FOR_ISSUER,
		});
	}, [append]);

	return {
		securitiesFields: fields,
		onRemoveSecurityRow,
		onAddSecurityRow,
	};
};
